/**
 * The hub's four non-date filters.
 *
 * Paged tabs send them to the server as `patient._tag` clauses on the Patient's
 * filter tags (BAR-2884); tabs that load their whole cohort still narrow the
 * loaded rows, each saying where a row keeps each value.
 */

import { Meta } from '@medplum/fhirtypes';
import { PATIENT_FILTER_TAG_SYSTEMS, PAYMENT_STATUS_TAG_SYSTEMS, Track } from '@bariendo/lib/constants';
import { isEmployerSponsoredTrack } from '@bariendo/lib/pac-inquiry-list';
import { PN_QUALIFICATION_LEVELS } from '@bariendo/lib/pn-consultation-note';
import { LEAD_STATUS_OPTIONS } from '@/components/LeadStatus/leadStatusOptions';

/**
 * `qualification` is Post-consultation's only (BAR-2975); `step`, the payment status, is the
 * financing and coverage tabs' (BAR-3095); `payor` is the Coverage tabs', each reading it its own
 * way (BAR-3097). Other tabs leave them at All.
 */
export type QueueFilterKey = 'pn' | 'track' | 'location' | 'status' | 'qualification' | 'step' | 'payor';

export type QueueFilterValues = Record<QueueFilterKey, string>;

/** "Everyone" / "All": the filter is off. */
export const QUEUE_FILTER_ANY = 'all';

/** The PN filter's "Unassigned": rows with no navigator. */
export const QUEUE_FILTER_UNASSIGNED = 'unassigned';

export interface QueueFilterFields<Row> {
  /** Every navigator on the row; a row matches if any of them is chosen. */
  pn: (row: Row) => string[];
  track: (row: Row) => string | undefined;
  location: (row: Row) => string | undefined;
  status: (row: Row) => string | undefined;
}

function matchesPn(pns: string[], value: string): boolean {
  if (value === QUEUE_FILTER_UNASSIGNED) {
    return pns.length === 0;
  }
  return pns.includes(value);
}

export function applyQueueFilters<Row>(rows: Row[], values: QueueFilterValues, fields: QueueFilterFields<Row>): Row[] {
  return rows.filter(
    (row) =>
      (values.pn === QUEUE_FILTER_ANY || matchesPn(fields.pn(row), values.pn)) &&
      (values.track === QUEUE_FILTER_ANY || fields.track(row) === values.track) &&
      (values.location === QUEUE_FILTER_ANY || fields.location(row) === values.location) &&
      (values.status === QUEUE_FILTER_ANY || fields.status(row) === values.status)
  );
}

export interface QueueFilterOption {
  value: string;
  label: string;
}

/** Payment track · Employer and insurance: the Coverage Won tab's default (BAR-3099). */
export const COVERAGE_TRACKS_FILTER = 'employer-insurance';

export const TRACK_FILTER_OPTIONS: QueueFilterOption[] = [
  { value: QUEUE_FILTER_ANY, label: 'All' },
  { value: 'd2c', label: 'Self-pay' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'employer', label: 'Employer' },
];

/** With Employer and insurance, the Coverage Won tab's default, after All (BAR-3099). */
export const COVERAGE_TRACK_FILTER_OPTIONS: QueueFilterOption[] = [
  TRACK_FILTER_OPTIONS[0],
  { value: COVERAGE_TRACKS_FILTER, label: 'Employer and insurance' },
  ...TRACK_FILTER_OPTIONS.slice(1),
];

/** Status · Active / Hold / Lost / All; a row's value is its `activeLead.status`. */
export const LEAD_STATUS_FILTER_OPTIONS: QueueFilterOption[] = [
  ...LEAD_STATUS_OPTIONS.map((option) => ({ value: option.status, label: option.label })),
  { value: QUEUE_FILTER_ANY, label: 'All' },
];

/** Qualified · the PN consultation Outcome, most committed first (BAR-2975). */
export const QUALIFICATION_FILTER_OPTIONS: QueueFilterOption[] = [
  { value: QUEUE_FILTER_ANY, label: 'All' },
  ...PN_QUALIFICATION_LEVELS.map((level) => ({ value: level.code, label: level.label })),
];

/**
 * A row's value for the Payment track filter: every employer-sponsored track
 * folds into `employer`; any other track is its own value, so an insurance
 * patient does not read as self-pay. A new non-employer track needs its own
 * entry in `TRACK_FILTER_OPTIONS`, or it is reachable only under "All".
 */
export function trackFilterValue(track: Track | undefined): string | undefined {
  if (track === undefined) {
    return undefined;
  }
  return isEmployerSponsoredTrack(track) ? 'employer' : track;
}

/** Whether a track passes the Payment track filter's value. */
export function trackFilterMatches(value: string, track: Track | undefined): boolean {
  const own = trackFilterValue(track);
  return value === COVERAGE_TRACKS_FILTER ? own === 'employer' || own === 'insurance' : own === value;
}

/**
 * The filters as `_filter` clauses on the Patient's tags; none for "All".
 * `prefix` is `patient.` from an Appointment-rooted query, empty from a Patient-rooted one.
 */
export function patientFilterClauses(values: QueueFilterValues, prefix = 'patient.'): string[] {
  const patientTagClause = (system: string, code: string): string => `${prefix}_tag eq "${system}|${code}"`;
  const clauses: string[] = [];

  if (values.pn !== QUEUE_FILTER_ANY) {
    clauses.push(patientTagClause(PATIENT_FILTER_TAG_SYSTEMS.pn, values.pn));
  }
  if (values.track !== QUEUE_FILTER_ANY) {
    // The tag holds the track itself; Employer covers every employer-sponsored one.
    const tracks = Object.values(Track).filter((track) => trackFilterMatches(values.track, track));
    clauses.push(`(${tracks.map((track) => patientTagClause(PATIENT_FILTER_TAG_SYSTEMS.track, track)).join(' or ')})`);
  }
  if (values.location !== QUEUE_FILTER_ANY) {
    clauses.push(patientTagClause(PATIENT_FILTER_TAG_SYSTEMS.location, values.location));
  }
  if (values.status !== QUEUE_FILTER_ANY) {
    clauses.push(patientTagClause(PATIENT_FILTER_TAG_SYSTEMS.status, values.status));
  }
  if (values.qualification !== QUEUE_FILTER_ANY) {
    clauses.push(patientTagClause(PATIENT_FILTER_TAG_SYSTEMS.qualification, values.qualification));
  }
  if (values.step !== QUEUE_FILTER_ANY) {
    clauses.push(patientTagClause(PAYMENT_STATUS_TAG_SYSTEMS.status, values.step));
  }

  return clauses;
}

/**
 * `patientFilterClauses`' rules in the browser, for rows that arrive by
 * subscription: those skip the query's `_filter`. Keep the two in step.
 */
export function matchesPatientFilterTags(patient: { meta?: Meta } | undefined, values: QueueFilterValues): boolean {
  const codes = (system: string): string[] =>
    (patient?.meta?.tag ?? []).filter((tag) => tag.system === system).map((tag) => tag.code ?? '');
  const has = (system: string, value: string): boolean => codes(system).includes(value);

  return (
    (values.pn === QUEUE_FILTER_ANY || has(PATIENT_FILTER_TAG_SYSTEMS.pn, values.pn)) &&
    (values.track === QUEUE_FILTER_ANY ||
      codes(PATIENT_FILTER_TAG_SYSTEMS.track).some((track) => trackFilterMatches(values.track, track as Track))) &&
    (values.location === QUEUE_FILTER_ANY || has(PATIENT_FILTER_TAG_SYSTEMS.location, values.location)) &&
    (values.status === QUEUE_FILTER_ANY || has(PATIENT_FILTER_TAG_SYSTEMS.status, values.status)) &&
    (values.qualification === QUEUE_FILTER_ANY ||
      has(PATIENT_FILTER_TAG_SYSTEMS.qualification, values.qualification)) &&
    (values.step === QUEUE_FILTER_ANY || has(PAYMENT_STATUS_TAG_SYSTEMS.status, values.step))
  );
}

/** A tab's `_filter` narrowed by the filters. */
export function withPatientFilters(filter: string, values: QueueFilterValues): string {
  const clauses = patientFilterClauses(values);
  return clauses.length > 0 ? `(${[filter, ...clauses].join(' and ')})` : filter;
}

/** Every filter off. A tab overrides the keys whose default is not "all" (Status · Active). */
export const QUEUE_FILTERS_OFF: QueueFilterValues = {
  pn: QUEUE_FILTER_ANY,
  track: QUEUE_FILTER_ANY,
  location: QUEUE_FILTER_ANY,
  status: QUEUE_FILTER_ANY,
  qualification: QUEUE_FILTER_ANY,
  step: QUEUE_FILTER_ANY,
  payor: QUEUE_FILTER_ANY,
};

/**
 * "All" plus each distinct value the loaded rows carry, sorted. With no values
 * the filter offers only its default, so a tab without the data cannot pretend
 * to filter by it.
 */
export function optionsFromRows(values: Array<string | undefined>, anyLabel: string): QueueFilterOption[] {
  const distinct = [...new Set(values.filter((value): value is string => Boolean(value)))].sort();
  return [{ value: QUEUE_FILTER_ANY, label: anyLabel }, ...distinct.map((value) => ({ value, label: value }))];
}

/** Whether anything is off its default — what decides that Reset shows. */
export function isQueueFiltered(values: QueueFilterValues, defaults: QueueFilterValues): boolean {
  return (Object.keys(defaults) as QueueFilterKey[]).some((key) => values[key] !== defaults[key]);
}
